import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_Row,
    type MRT_TableOptions,
    useMaterialReactTable,
    MRT_TableInstance,
} from 'material-react-table'
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormHelperText,
    IconButton,
    Input,
    InputAdornment,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { useAppSelector } from '../../../redux/hook'
import { DragIndicatorOutlined, OpenInNewOutlined } from '@mui/icons-material'
import { useCreateAttribute } from './hooks/createAttribute'
import { useGetAttributes } from './hooks/getAttributes'
import { useUpdateAttribute } from './hooks/updateAttribute'
import { useDeleteAttribute } from './hooks/deleteAttribute'
import { AttributeMetadata, EntityType, IAttribute } from '../interfaces'
import ConfirmationDialog from '../../Common/ConfirmationDialog'
import { flattenEntityTemplates } from '../../../utils/EntityUtils'
import {
    AttributeType,
    TemplateProperties,
    UserPermissions,
} from '../../../constants/constants'
import TruncatedText from '../../Common/TruncatedText'
import KeyValueField from './KeyValueField'
import { v4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import CustomAttributeField from './CustomAttributeField'
import { usePermissions } from '../../../hooks/usePermissions'

const attributeTypes = ['Static', 'Dynamic', 'Both']

type EntityTypeDropdownValuesType = {
    id: string
    name: string
}

type RangeFieldType = 'text' | 'select' | 'object' | 'custom field'

export interface CustomAttributeFieldDataType {
    name: string
    templateId: string
    attributeId: string
    experssion: string
}

const Attributes = (): ReactElement => {
    const { t } = useTranslation()
    const org = useAppSelector((state) => state.org.org)
    const selectedEntity = useAppSelector(
        (state) => state.dtStudio.selectedEntity,
    )
    const { id: entityId } = selectedEntity
    const orgId = org?.id ? org?.id : ''

    const dataTypes = useAppSelector((state) => state.dtStudio.dataTypes)
    const metaDataOptions = useAppSelector(
        (state) => state.dtStudio.attributeMetaDataOptions,
    )

    const navList = useAppSelector((state) => state.nav.navList)
    const { hasPermission } = usePermissions()
    const hasWriteAccess = useMemo(() => {
        return (
            !!selectedEntity?.ownTemplate &&
            hasPermission(UserPermissions.ENTITY_WRITER)
        )
    }, [selectedEntity?.ownTemplate, hasPermission])
    const hasPartialEditAccess = useMemo(() => {
        return hasPermission(UserPermissions.ENTITY_WRITER)
    }, [hasPermission])

    const handleAttributeMetaDataSelection = (properties: string[]) => {
        setSelectedMetaProperties(properties)
    }

    const [validationErrors, setValidationErrors] = useState<
        Record<string, string | undefined>
    >({})
    const [editedRowId, setEditedRowId] = useState<string | null>(null)
    const [selectedType, setSelectedType] = useState<string>('') //based on this, need to enable madatory column
    const [requiredField, setRequiredField] = useState<boolean>(false)

    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    const [deleteRow, setDeleteRow] = useState<MRT_Row<IAttribute> | null>(null)
    const [selectedMetaProperties, setSelectedMetaProperties] = useState<
        string[]
    >([])

    const [columnVisibility, setColumnVisibility] = useState({
        id: false,
    })

    const [totalEntitesList, setTotalEntitesList] = useState<EntityType[]>([])
    const [entityTypeDropdownValues, setEntityTypeDropdownValues] = useState<
        EntityTypeDropdownValuesType[]
    >([])

    const [rangeEdit, setRangeEdit] = useState<RangeFieldType>('text')

    const [range, setRange] = useState<any>('')

    const [defaultValue, setDefaultValue] = useState('')

    const [isKeyValueFieldOpen, setIsKeyValueFieldOpen] =
        useState<boolean>(false)

    const [attributeMetaDataOptions, setAttributeMetaDataOptions] = useState<
        string[]
    >([])

    const columns = useMemo<MRT_ColumnDef<IAttribute>[]>(
        () => [
            {
                accessorKey: 'id',
                // name: 'id',
                header: t('attributeName'),
                enableEditing: (row) => {
                    return hasWriteAccess && !row.original.readOnly
                },
                muiEditTextFieldProps: {
                    placeholder: t('enterName'),
                    required: true,
                    error: !!validationErrors?.id,
                    helperText: validationErrors?.id,
                    //remove any previous validation errors when user focuses on the input
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            id: undefined,
                        }),
                    //optionally add validation checking for onBlur or onChange
                },
                Cell: (row) => (
                    <Box sx={{ width: '100%' }}>
                        <TruncatedText
                            sx={{ fontWeight: '400', fontSize: '14px' }}
                            text={row.row.original['id']}
                        />
                    </Box>
                ),
            },
            {
                accessorKey: 'name',
                header: t('displayName'),
                enableEditing: true,
                muiEditTextFieldProps: {
                    placeholder: t('enterDisplayName'),
                    required: true,
                    error: !!validationErrors?.name,
                    helperText: validationErrors?.name,
                    //remove any previous validation errors when user focuses on the input
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            name: undefined,
                        }),
                    //optionally add validation checking for onBlur or onChange
                },

                Cell: (row) => (
                    <Box sx={{ width: '100%' }}>
                        <TruncatedText
                            sx={{ fontWeight: '400', fontSize: '14px' }}
                            text={row.row.original['name']}
                        />
                    </Box>
                ),
            },
            {
                accessorKey: 'dataType',
                header: t('dataType'),
                editVariant: 'select',
                editSelectOptions: dataTypes,
                enableEditing: (row) => {
                    return hasWriteAccess && !row.original.readOnly
                },
                muiEditTextFieldProps: ({ row }) => {
                    return {
                        placeholder: t('chooseDataType'),
                        select: true,
                        error: !!validationErrors?.dataType,
                        helperText: validationErrors?.dataType,
                        onChange: (e) => {
                            onChangeDataType(e.target.value, row)
                        },
                    }
                },
            },
            {
                id: 'range',
                header: t('range'),
                enableEditing: true,
                accessorFn: (originalRow) => {
                    return originalRow?.range
                },

                Cell: (row) => {
                    let text = ''
                    if (
                        row.row.original.dataType.toLowerCase() ===
                            AttributeType.ENTITY ||
                        row.row.original.dataType.toLowerCase() ===
                            AttributeType.Entity_JSON ||
                        row.row.original.dataType.toLowerCase() ===
                            AttributeType.JSON ||
                        row.row.original.dataType.toLowerCase() ===
                            AttributeType.RULESET
                    ) {
                        text =
                            totalEntitesList.find(
                                (i) => i.id === row.row.original.range,
                            )?.name || ''
                    } else if (
                        row.row.original.dataType.toLowerCase() ===
                        AttributeType.CUSTOM_ATTRIBUTE
                    ) {
                        const range = row.row.original?.range?.trim()
                        const value = range ? JSON.parse(range) : ''
                        text = value
                            ? value?.relationName + ', ' + value?.attributeName
                            : ''
                    } else {
                        text = row.row.original?.range
                    }

                    return (
                        <Box sx={{ width: '100%' }}>
                            <TruncatedText
                                sx={{ fontWeight: '400', fontSize: '14px' }}
                                text={text}
                            />
                        </Box>
                    )
                },
                Edit: () => {
                    let rangeField
                    switch (rangeEdit) {
                        case 'select':
                            rangeField = (
                                <Select
                                    id="select-attribute-field-Type"
                                    value={range}
                                    onChange={(event: any) => {
                                        setRange(event.target.value)
                                    }}
                                    label=""
                                    variant="standard"
                                    required={true}
                                    sx={{ width: '100%' }}
                                >
                                    {entityTypeDropdownValues?.map((s) => (
                                        <MenuItem key={s.id} value={s.id}>
                                            {s.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )
                            break
                        case 'object':
                            rangeField = (
                                <Input
                                    id="select-attribute-field-Type"
                                    value={range}
                                    onChange={() => {
                                        setIsKeyValueFieldOpen(true)
                                    }}
                                    // label=""
                                    // variant="standard"
                                    // disabled={true}
                                    required={true}
                                    sx={{ width: '100%' }}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => {
                                                    setIsKeyValueFieldOpen(true)
                                                }}
                                            >
                                                <OpenInNewOutlined />
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                            )
                            break
                        case 'custom field':
                            rangeField = (
                                <CustomAttributeField
                                    data={range}
                                    onChange={(
                                        data: CustomAttributeFieldDataType,
                                    ) => {
                                        setRange(JSON.stringify(data))
                                    }}
                                />
                            )
                            break
                        default:
                            rangeField = (
                                <TextField
                                    id="select-attribute-field-Type"
                                    value={range}
                                    onChange={(event: any) => {
                                        setRange(event.target.value)
                                    }}
                                    label=""
                                    variant="standard"
                                    required={true}
                                    sx={{ width: '100%' }}
                                />
                            )
                            break
                    }
                    return (
                        <FormControl
                            fullWidth
                            error={!!validationErrors?.range}
                        >
                            {rangeField}
                            <FormHelperText>
                                {validationErrors?.range}
                            </FormHelperText>
                        </FormControl>
                    )
                },
            },
            {
                accessorKey: 'defaultValue',
                header: t('defaultValue'),
                enableEditing: hasWriteAccess,
                muiEditTextFieldProps: () => {
                    return {
                        // type: 'email', To be decided later - by MB
                        placeholder: t('enterDefaultValue'),
                        required: false,
                        error: !!validationErrors?.defaultValue,
                        helperText: validationErrors?.defaultValue,
                        value: defaultValue,
                        onChange: (e) => {
                            setDefaultValue(e.target.value)
                        },
                        //remove any previous validation errors when user focuses on the input
                        onFocus: () =>
                            setValidationErrors({
                                ...validationErrors,
                                defaultValue: undefined,
                            }),
                    }
                },
            },
            {
                accessorKey: 'attributeType',
                header: t('attributeType'),
                enableEditing: (row) => {
                    return hasWriteAccess && !row.original.readOnly
                },
                Edit: () => {
                    return (
                        <FormControl
                            fullWidth
                            error={!!validationErrors?.attributeType}
                        >
                            <Select
                                id="select-attributeType"
                                value={selectedType}
                                onChange={(event: any) => {
                                    setSelectedType(event.target.value)
                                }}
                                label=""
                                variant="standard"
                                required={true}
                                sx={{ width: '100%' }}
                            >
                                {attributeTypes?.map((s: string) => (
                                    <MenuItem key={s} value={s}>
                                        {s}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                {validationErrors?.attributeType}
                            </FormHelperText>
                        </FormControl>
                    )
                },
            },
            {
                accessorKey: 'required',
                header: t('mandatory'),
                enableEditing: true,
                Edit: () => {
                    return (
                        <Checkbox
                            id="required-checkbox"
                            value={requiredField}
                            checked={requiredField}
                            disabled={selectedType.toLowerCase() === 'dynamic'}
                            onChange={() => {
                                setRequiredField(!requiredField)
                            }}
                        />
                    )
                },
                Cell: ({ row }) => (
                    <Checkbox
                        checked={row.original.required}
                        // sx={{ cursor: 'none' }}
                    />
                ),
            },
            {
                accessorKey: 'metadata',
                header: t('properties'),
                enableEditing: true,
                Cell: ({ row }) => {
                    const selectedProperties = Object.keys(
                        row.original.metadata || {},
                    ).filter(
                        (key) =>
                            row.original.metadata[
                                key as keyof AttributeMetadata
                            ],
                    )
                    return (
                        <Box sx={{ width: '100%' }}>
                            <TruncatedText
                                sx={{ fontWeight: '400', fontSize: '14px' }}
                                text={selectedProperties.join(', ')}
                            />
                        </Box>
                    )
                },
                Edit: ({ row }) => (
                    <PropertiesCell
                        row={row}
                        attributeMetaDataOptions={attributeMetaDataOptions}
                        handleAttributeMetaDataSelection={
                            handleAttributeMetaDataSelection
                        }
                    />
                ),
            },
        ],
        [
            validationErrors,
            editedRowId,
            requiredField,
            selectedType,
            attributeMetaDataOptions,
            selectedEntity,
            rangeEdit,
            entityTypeDropdownValues,
            range,
            isKeyValueFieldOpen,
            defaultValue,
            totalEntitesList,
            attributeMetaDataOptions,
            hasWriteAccess,
        ],
    )

    useEffect(() => {
        if (selectedType.toLowerCase() === 'dynamic') {
            setRequiredField(false)
        }
    }, [selectedType])

    useEffect(() => {
        const getTotalList = () => {
            let updatedTotalEntityList = flattenEntityTemplates(navList)
            updatedTotalEntityList = updatedTotalEntityList.filter(
                (i: any) => i.id !== selectedEntity?.id,
            )
            setTotalEntitesList(updatedTotalEntityList)
        }
        getTotalList()
    }, [navList, selectedEntity])

    useEffect(() => {
        if (hasWriteAccess) {
            setColumnVisibility({ id: true })
        } else {
            setColumnVisibility({ id: false })
        }
        clearEditOrAddValues()
    }, [hasWriteAccess])

    useEffect(() => {
        if (editedRowId === null) {
            setRequiredField(false)
            setSelectedType('')
        }
    }, [editedRowId])

    const validateRequired = (value: string) => !!value?.length

    const validateAttribute = (attribute: IAttribute) => {
        return {
            id: hasWriteAccess
                ? !validateRequired(attribute.id)
                    ? t('attributeNameRequired')
                    : ''
                : '',
            name: !validateRequired(attribute.name)
                ? t('attributeDisplayNameRequired')
                : '',
            dataType: !validateRequired(attribute.dataType)
                ? t('dataTypeRequired')
                : '',
            // range: !validateRequired(attribute.range) ? t('rangeRequired') : '',
            attributeType: !validateRequired(attribute.attributeType)
                ? t('attributeTypeRequired')
                : '',
        }
    }

    const onChangeDataType = (type: string, row: MRT_Row<IAttribute>) => {
        let updateEntityTypeDropdownValues = []
        if (
            type?.toLowerCase() === AttributeType.Entity_JSON ||
            type?.toLowerCase() === AttributeType.JSON
        ) {
            updateEntityTypeDropdownValues = totalEntitesList
                .filter(
                    (item) => item.properties[TemplateProperties.ENTITY_JSON],
                )
                ?.map((i: any) => {
                    return { ...{ name: i.name, id: i.id } }
                })
        }
        if (type?.toLowerCase() === AttributeType.RULESET) {
            updateEntityTypeDropdownValues = totalEntitesList
                .filter((item) => item.properties[TemplateProperties.RULESET])
                ?.map((i: any) => {
                    return { ...{ name: i.name, id: i.id } }
                })
        } else {
            updateEntityTypeDropdownValues = totalEntitesList.map((i: any) => {
                return { ...{ name: i.name, id: i.id } }
            })
        }

        let newRangeEdit: RangeFieldType = 'text'
        if (
            type?.toLowerCase() === AttributeType.ENTITY ||
            type?.toLowerCase() === AttributeType.Entity_JSON ||
            type?.toLowerCase() === AttributeType.JSON ||
            type?.toLowerCase() === AttributeType.RULESET
        ) {
            newRangeEdit = 'select'
        } else if (
            type?.toLowerCase() === AttributeType.MULTI_SELECT ||
            type?.toLowerCase() === AttributeType.SELECT
        ) {
            newRangeEdit = 'object'
        } else if (type?.toLowerCase() === AttributeType.CUSTOM_ATTRIBUTE) {
            newRangeEdit = 'custom field'
        }

        const updatedAttributeMetaDataOptions = [
            ...(metaDataOptions?.default || []),
            ...(metaDataOptions?.[type] || []),
        ]

        setEntityTypeDropdownValues(updateEntityTypeDropdownValues)
        setRangeEdit(newRangeEdit)
        setRange(row?.original?.range)
        setAttributeMetaDataOptions(updatedAttributeMetaDataOptions)
    }

    const onSaveRangeForKeyValue = (val: any) => {
        setRange(JSON.stringify(val))
        setIsKeyValueFieldOpen(false)
    }

    //call CREATE hook
    const { mutateAsync: createAttribute, isPending: isCreatingAttribute } =
        useCreateAttribute(orgId, entityId)
    //call READ hook
    const {
        data: fetchedAttributes = [],
        isError: isLoadingAttributesError,
        isFetching: isFetchingAttributes,
        isLoading: isLoadingAttributes,
    } = useGetAttributes(orgId, entityId)
    //call UPDATE hook
    const { mutateAsync: updateAttribute, isPending: isUpdatingAttribute } =
        useUpdateAttribute(orgId, entityId)
    //call DELETE hook
    const { mutateAsync: deleteAttribute, isPending: isDeletingAttribute } =
        useDeleteAttribute(orgId, entityId)

    //CREATE action
    const handleCreateAttribute: MRT_TableOptions<IAttribute>['onCreatingRowSave'] =
        async ({ values }) => {
            values.attributeType = selectedType
            const metaPropertiesObject = selectedMetaProperties.reduce<
                Record<string, boolean>
            >((acc, property) => {
                acc[property] = true
                return acc
            }, {})
            values.metadata = metaPropertiesObject
            const newValidationErrors = validateAttribute(values)
            if (Object.values(newValidationErrors).some((error) => error)) {
                setValidationErrors(newValidationErrors)
                return
            }
            setValidationErrors({})

            values.required = requiredField

            values.range = range

            await createAttribute(values)
            clearEditOrAddValues()
        }

    //UPDATE action
    const handleSaveAttribute: MRT_TableOptions<IAttribute>['onEditingRowSave'] =
        async ({ values, row }) => {
            const newValidationErrors = validateAttribute(values)
            if (Object.values(newValidationErrors).some((error) => error)) {
                setValidationErrors(newValidationErrors)
                return
            }
            setValidationErrors({})
            values.required = requiredField
            values.attributeType = selectedType
            const metaPropertiesObject = selectedMetaProperties.reduce<
                Record<string, boolean>
            >((acc, property) => {
                acc[property] = true
                return acc
            }, {})
            values.metadata = metaPropertiesObject
            values.required = requiredField

            values.range = range

            const isMetaDataUpdated = metaDataOptions?.default?.some((key) => {
                const originalValue =
                    row.original.metadata[key as keyof AttributeMetadata]
                const newValue = values.metadata[key]
                return originalValue !== newValue
            })

            await updateAttribute({
                ...values,
                uid: row.original.uid,
                id: hasWriteAccess ? values?.id : row.original.uid,
                sequence: row.original.sequence,
                isDisplayNameUpdated: values?.name !== row.original.name,
                isPropertiesUpdated:
                    values?.required !== row.original.required ||
                    values?.range !== row.original.range ||
                    isMetaDataUpdated,
            })
            clearEditOrAddValues()
        }
    const handleRowDrag = async (
        draggingRow: MRT_Row<IAttribute>,
        hoveredRow: Partial<MRT_Row<IAttribute>>,
    ) => {
        const newSequnce = hoveredRow?.original?.sequence
        const newIndex = hoveredRow?.index
        const oldIndex = draggingRow.index
        if (
            newSequnce === undefined ||
            newIndex === undefined ||
            newIndex === oldIndex
        ) {
            return
        }
        const attribute: IAttribute = {
            ...draggingRow.original,
            uid: draggingRow.original.uid,
            sequence: newSequnce,
        }

        queryClient.setQueryData(
            ['attributes', entityId, orgId],
            (prevAttributes: IAttribute[]) => {
                const updatedAttributes = [...prevAttributes]
                if (newIndex > oldIndex) {
                    for (let i = oldIndex + 1; i <= newIndex; i++) {
                        updatedAttributes[i].sequence -= 1
                    }
                } else {
                    for (let i = newIndex; i < oldIndex; i++) {
                        updatedAttributes[i].sequence += 1
                    }
                }
                updatedAttributes.splice(oldIndex, 1)
                updatedAttributes.splice(newIndex, 0, attribute)
                return updatedAttributes
            },
        )
        await updateAttribute(attribute)
    }

    //DELETE action
    const onClickRowDelete = (row: MRT_Row<IAttribute>) => {
        setDeleteRow(row)
        setOpenDeleteDialog(true)
    }

    const handleDeleteDialogOperation = () => {
        const deletedID = deleteRow ? deleteRow.original.id : ''
        deleteAttribute(deletedID)
        setOpenDeleteDialog(false)
    }

    //Clearing Values on Cancel
    const clearEditOrAddValues = () => {
        table.setCreatingRow(null)
        table.setEditingRow(null)
        setValidationErrors({})
        setRequiredField(false)
        setSelectedType('')
        setRange('')
        setDefaultValue('')
        setEditedRowId(null)
        setSelectedMetaProperties([])
    }

    const addSaparator = async () => {
        const saparator: any = {
            id: v4(),
            name: v4(),
            dataType: AttributeType.SEPARATOR,
            attributeType: 'Static',
            required: false,
            metadata: {},
            isSearchable: false,
            readOnly: true,
        }
        await createAttribute(saparator)
    }

    const queryClient = useQueryClient()

    const IconDragProps = (
        row: MRT_Row<IAttribute>,
        table: MRT_TableInstance<IAttribute>,
    ) => {
        return {
            onDragEnd: () => {
                const { draggingRow, hoveredRow } = table.getState()
                if (hoveredRow && draggingRow) {
                    if (
                        hoveredRow.original?.readOnly ||
                        draggingRow.original.readOnly
                    ) {
                        //We should not reorder the inhereted attributes
                        return
                    }
                    handleRowDrag(draggingRow, hoveredRow)
                }
            },
            disabled: row.original?.readOnly || !hasWriteAccess,
            sx: {
                opacity: row.original?.readOnly || !hasWriteAccess ? '0.5' : '',
            },
        }
    }

    const table = useMaterialReactTable({
        columns,
        data: fetchedAttributes,
        createDisplayMode: 'row',
        editDisplayMode: 'row',
        enableEditing: true,
        enableRowOrdering: true,
        positionActionsColumn: 'last',
        enableColumnActions: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableSorting: false,
        enableTopToolbar: false,
        enablePagination: false,
        enableStickyHeader: true,
        getRowId: (row) => row.id,
        layoutMode: 'grid',
        positionCreatingRow: 'bottom',
        displayColumnDefOptions: {
            'mrt-row-actions': { header: '', size: 120 },

            'mrt-row-drag': {
                header: t('move'),
                size: 20,

                muiTableHeadCellProps: {
                    sx: { fontWeight: 'bold', textAlign: 'center' },
                },
            },
        },
        muiToolbarAlertBannerProps: isLoadingAttributesError
            ? {
                  color: 'error',
                  children: t('errorLoadingData'),
              }
            : undefined,
        muiTableHeadCellProps: {
            sx: {
                border: 'none',
                fontSize: '14px',
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: 'none',
            },
        },
        muiBottomToolbarProps: {
            sx: {
                boxShadow: 'none',
                alignItems: 'center',
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: `calc(100vh - ${hasWriteAccess ? '420px' : '370px'})`,
                // maxHeight: '100%',
            },
        },
        muiTableBodyRowProps: ({ row }) => ({
            sx:
                row?.original?.dataType?.toLowerCase() ===
                AttributeType.SEPARATOR
                    ? {
                          backgroundColor: 'transparent',
                          '&:hover': {
                              '& td::after': {
                                  background: 'none',
                              },
                          },
                          '& td:not(:first-of-type):not(:last-child)': {
                              padding: '0',
                              height: '3px',
                              background: '#ccc',
                              color: '#ccc',
                              margin: 'auto 0px ',
                          },
                          '& td:not(:first-of-type):not(:last-child) > * ': {
                              display: 'none',
                          },
                      }
                    : {},
        }),
        muiTableBodyCellProps: () => ({
            sx: { border: 'none', fontSize: '14px' },
        }),
        icons: {
            DragHandleIcon: () => <DragIndicatorOutlined id="dragIcon" />,
        },
        muiRowDragHandleProps: (props: {
            row: MRT_Row<IAttribute>
            table: MRT_TableInstance<IAttribute>
        }) => IconDragProps(props.row, props.table),
        onCreatingRowCancel: () => {
            clearEditOrAddValues()
        },
        onCreatingRowSave: handleCreateAttribute,
        onEditingRowCancel: () => {
            clearEditOrAddValues()
        },
        onEditingRowSave: handleSaveAttribute,
        renderRowActions: ({ row, table }) => (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
                {row?.original?.dataType?.toLowerCase() ===
                    AttributeType.SEPARATOR || !hasPartialEditAccess ? (
                    <Box width="40px"> </Box>
                ) : (
                    <Tooltip title={t('edit')}>
                        <IconButton
                            id={row.original.name + 'edit'}
                            onClick={() => {
                                table.setEditingRow(row)
                                setEditedRowId(row.original.id)
                                setRequiredField(row.original.required)
                                setSelectedType(row.original.attributeType)
                                onChangeDataType(row.original.dataType, row)
                                setRange(row.original.range)
                                setDefaultValue(row.original.defaultValue)
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                )}
                {!row.original.readOnly && hasWriteAccess ? (
                    <Tooltip title={t('delete')}>
                        <IconButton
                            id={row.original.name + 'delete'}
                            color="error"
                            onClick={() => onClickRowDelete(row)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
            </Box>
        ),
        enableBottomToolbar: hasWriteAccess,
        renderBottomToolbarCustomActions: ({ table }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Button
                    id={'add-new-attribute'}
                    variant="contained"
                    disabled={
                        !!table.getState().editingRow ||
                        !!table.getState().creatingRow
                    }
                    onClick={() => {
                        table.setCreatingRow(true)
                        setRangeEdit('text')
                        setRange('')
                        setDefaultValue('')
                    }}
                >
                    {t('addNewAttribute')}
                </Button>
                <Button
                    id="add-new-separator"
                    variant="contained"
                    disabled={
                        !!table.getState().editingRow ||
                        !!table.getState().creatingRow
                    }
                    onClick={() => addSaparator()}
                >
                    {t('addSeparator')}
                </Button>
            </Box>
        ),
        defaultColumn: {
            minSize: 50, //allow columns to get smaller than default
            maxSize: 9001, //allow columns to get larger than default
            size: 200, //make columns wider by default
            grow: true,
        },

        state: {
            isLoading: isLoadingAttributes,
            isSaving:
                isCreatingAttribute ||
                isUpdatingAttribute ||
                isDeletingAttribute,
            showAlertBanner: isLoadingAttributesError,
            showProgressBars: isFetchingAttributes,
            density: 'compact',
            columnVisibility,
        },
    })

    return (
        <>
            <MaterialReactTable table={table} />
            <ConfirmationDialog
                openDialog={openDeleteDialog}
                handleComfirm={() => handleDeleteDialogOperation()}
                handleClose={() => setOpenDeleteDialog(false)}
                DialogHeaderText={t('confirmDelete')}
                DialogBodyText={t('confirmDeleteItem', {
                    item:
                        deleteRow?.original?.dataType ===
                        AttributeType.SEPARATOR
                            ? t('separator')
                            : t('attributeSmall'),
                })}
            />
            {isKeyValueFieldOpen && (
                <KeyValueField
                    list={range}
                    onSave={(val: any) => {
                        onSaveRangeForKeyValue(val)
                    }}
                    onClose={() => {
                        setIsKeyValueFieldOpen(false)
                    }}
                />
            )}
        </>
    )
}

const PropertiesCell = ({
    row,
    attributeMetaDataOptions,
    handleAttributeMetaDataSelection,
}: {
    row: MRT_Row<IAttribute>
    attributeMetaDataOptions: string[]
    handleAttributeMetaDataSelection: (properties: string[]) => void
}) => {
    const [selectedProperties, setSelectedProperties] = useState<string[]>([])

    useEffect(() => {
        const metadata = row.original.metadata || {}
        const selected = attributeMetaDataOptions.filter(
            (property) => metadata[property as keyof AttributeMetadata],
        )
        setSelectedProperties(selected)
    }, [row.original.metadata, attributeMetaDataOptions])

    useEffect(() => {
        handleAttributeMetaDataSelection(selectedProperties)
    }, [selectedProperties])

    const handleChange = (
        event: SelectChangeEvent<typeof selectedProperties>,
    ) => {
        const value = event.target.value
        const newSelectedProperties =
            typeof value === 'string' ? [value] : value
        setSelectedProperties(newSelectedProperties)
    }
    return (
        <FormControl sx={{ width: '100%', maxWidth: '200px' }}>
            <Select
                id="select-metadata"
                multiple
                value={selectedProperties}
                onChange={handleChange}
                renderValue={useCallback(
                    (selected: any) => (
                        <Box>
                            <Box width="100%">
                                <TruncatedText text={selected.join(', ')} />
                            </Box>
                        </Box>
                    ),
                    [selectedProperties],
                )}
            >
                {attributeMetaDataOptions.map((property) => (
                    <MenuItem
                        key={property}
                        id={property}
                        value={property}
                        sx={{ padding: '0px' }}
                    >
                        <Checkbox
                            id={property + 'checkbox'}
                            checked={selectedProperties.includes(property)}
                        />
                        <ListItemText primary={property} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}

export default Attributes
